import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileAlt, FaUserGraduate, FaCheck, FaChevronDown } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';

const AssignPaper = () => {
  const [papers, setPapers] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [selectedProfessors, setSelectedProfessors] = useState({});
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = authService.getCurrentUser();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (currentUser.role !== 'admin') {
      navigate('/');
      return;
    }

    loadPapers();
    loadProfessors();
  }, [navigate]);

  const loadPapers = () => {
    const raw = localStorage.getItem('papers');
    let allPapers = [];
    try {
      allPapers = JSON.parse(raw) || [];
    } catch (e) { allPapers = []; }

    if (!Array.isArray(allPapers)) allPapers = [];

    // Sort by submission date in descending order (LIFO - newest first)
    const sortedPapers = allPapers.sort((a, b) => {
      const dateA = new Date(a.submittedAt);
      const dateB = new Date(b.submittedAt);
      return dateB - dateA; // Descending order (newest first)
    });

    setPapers(sortedPapers);
  };

  const loadProfessors = () => {
    const raw = localStorage.getItem('paperPortalUsers');
    let allUsers = [];
    try {
      allUsers = JSON.parse(raw) || [];
    } catch (e) { allUsers = []; }

    if (!Array.isArray(allUsers)) allUsers = [];

    const professorsList = allUsers.filter(user => user && (user.role === 'professor' || user.role === 'reviewer'));
    setProfessors(professorsList);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleProfessorSelect = (paperId, professorId) => {
    setSelectedProfessors({
      ...selectedProfessors,
      [paperId]: professorId
    });
  };

  const handleAssignSubmit = (paper) => {
    const selectedProfessorId = selectedProfessors[paper.id];

    if (!selectedProfessorId) {
      showToast('Please select a professor', 'error');
      return;
    }

    const professor = professors.find(p => p.id === selectedProfessorId);

    // Update paper with assignment
    const updatedPapers = papers.map(pap => {
      if (pap.id === paper.id) {
        return {
          ...pap,
          assignedProfessor: professor.name, // Professor name for display
          assignedTo: {
            professorId: professor.id,
            professorName: professor.name,
            professorEmail: professor.email,
            assignedAt: new Date().toISOString(),
            assignedBy: authService.getCurrentUser().name
          },
          status: 'in_queue', // Set to 'in_queue'
          inQueueAt: new Date().toISOString() // Add in_queue timestamp
        };
      }
      return pap;
    });

    localStorage.setItem('papers', JSON.stringify(updatedPapers));
    loadPapers();

    // Clear selection for this paper
    const newSelections = { ...selectedProfessors };
    delete newSelections[paper.id];
    setSelectedProfessors(newSelections);

    showToast(`Paper assigned to ${professor.name} successfully!`, 'success');
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Assign Papers</h1>
                <p className="text-green-100">Assign submitted papers to professors or reviewers for review</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaFileAlt className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Papers List */}
          {papers.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Papers Yet</h3>
              <p className="text-gray-600">No papers have been submitted for review.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {papers.map((paper) => (
                <div
                  key={paper.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 border-green-500"
                >
                  <div className="p-6">
                    {/* Assignment Status or All 4 Items in One Row */}
                    {paper.assignedTo ? (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FaCheck className="text-green-600" />
                          <p className="font-semibold text-green-800">Already Assigned</p>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong>Assigned To:</strong> {paper.assignedTo.professorName} ({paper.assignedTo.role || 'Professor'})
                        </p>
                        <p className="text-sm text-gray-700">
                          <strong>Email:</strong> {paper.assignedTo.professorEmail}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Assigned on {new Date(paper.assignedTo.assignedAt).toLocaleDateString()} by {paper.assignedTo.assignedBy}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                        {/* Paper Title */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border-2 border-green-200">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Paper Title</p>
                          <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={paper.paperTitle}>
                            {paper.paperTitle}
                          </p>
                        </div>

                        {/* Author Name */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Author Name</p>
                          <p className="font-semibold text-gray-900 text-sm">{paper.userName}</p>
                        </div>

                        {/* Professor Dropdown */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1 font-semibold">
                            Select Professor / Reviewer <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={selectedProfessors[paper.id] || ''}
                              onChange={(e) => handleProfessorSelect(paper.id, e.target.value)}
                              className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white transition-all hover:border-gray-300 text-sm font-semibold"
                            >
                              <option value="">Choose professor</option>
                              {professors.map((professor) => (
                                <option key={professor.id} value={professor.id}>
                                  {professor.name}
                                </option>
                              ))}
                            </select>
                            <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none text-xs" />
                          </div>
                        </div>

                        {/* Assign Button */}
                        <div>
                          <button
                            onClick={() => handleAssignSubmit(paper)}
                            disabled={!selectedProfessors[paper.id]}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all text-sm ${selectedProfessors[paper.id]
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                          >
                            <FaUserGraduate />
                            Assign
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AssignPaper;